<template>
  <article class="menu-option">
    <div class="menu-option__left">
      <div class="menu-option__image">
        <MenuImage
          :menu-item="menu"
          :badge="menu.Badge"
          badge-size="large"
          :main-menu-badge-active="mainMenuBadgeActive"
          :ad-image-path="menu.ADImagePath"
        />
      </div>
      <div class="menu-option__name">{{ getNameByLocale(menu) }}</div>
      <div class="menu-option__price">{{ formatCurrency(menu.FinalPrice) }}</div>
      <div class="menu-option__note">{{ getNoteByLocale(menu) }}</div>
    </div>
    <div class="menu-option__center">
      <MenuOptionGroupList
        :menu="menu"
        :topping-groups="toppingGroups"
        :set-menu-groups="setMenuGroups"
        :column-count="1"
        @change="handleOptionChange"
      />
    </div>
    <div class="menu-option__right">
      <div class="menu-option__selected-title">{{ t('title.selected_options') }}</div>
      <ul class="menu-option__selected-list">
        <li v-for="option in selectedTotalOptions">
          {{ getToppingGroupName(option.GroupCode) }}:
          {{ getOptionName(option.MenuItemCode, option.GroupCode, option.OptionCode) }}
          <span v-if="option.Qty > 1"> x{{ option.Qty }}</span>
          <span v-if="getOptionPrice(option) > 0"> (+{{ formatCurrency(getOptionPrice(option)) }})</span>
        </li>
      </ul>
      <div class="menu-option__selected-qty">
        <BaseCounter v-model="qty" :disabled="false" :limit="limitCount" />
      </div>
      <div class="menu-option__total-amount">
        <div class="menu-option__total-label">{{ t('label.total_amount') }}</div>
        <div class="menu-option__total-value">{{ formatCurrency(menuTotalAmount) }}</div>
      </div>
      <div class="menu-option__button-group">
        <BaseButton2 class="outline" @button-click="handleCloseClick">
          {{ t('button.close') }}
        </BaseButton2>
        <BaseButton2 variant="primary" v-if="!configStore.isMobileMenu" @button-click="handleAddCartClick">
          {{ t('button.cart_add') }}
        </BaseButton2>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
  import BaseButton2 from '@/components/base/BaseButton2.vue';
  import BaseCounter from '@/components/base/BaseCounter.vue';
  import MenuImage from '@/components/MenuImage.vue';
  import MenuOptionGroupList from '@/components/MenuOptionGroupList.vue';
  import { useMenu } from '@/composable/useMenu';
  import UserError from '@/error';
  import CartMenu from '@/interfaces/CartMenu';
  import CartMenuOption from '@/interfaces/CartMenuOption';
  import Menu from '@/interfaces/Menu';
  import SetMenuGroup from '@/interfaces/SetMenuGroup';
  import ToppingGroup from '@/interfaces/ToppingGroup';
  import { useCartStore } from '@/stores/cart';
  import { useConfigStore } from '@/stores/config';
  import { useMenuStore } from '@/stores/menu';
  import { formatCurrency, getNameByLocale, getNoteByLocale } from '@/utils';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = defineProps<{
    menu: Menu;
    qty?: number;
  }>();
  const emits = defineEmits(['close']);

  const { t } = useI18n();
  const { isMainMenuBadgeActive } = useMenu();
  const configStore = useConfigStore();
  const menuStore = useMenuStore();
  const cartStore = useCartStore();

  // 토핑 그룹 리스트 조회
  const toppingGroups: ToppingGroup[] =
    props.menu.MenuType === 'M'
      ? menuStore.toppingGroups.filter((toppingGroup) => toppingGroup.TemplateCode === props.menu.ToppingTemplateCode)
      : [];

  // 세트메뉴 그룹 리스트 조회
  const setMenuGroups: SetMenuGroup[] =
    props.menu.MenuType === 'S'
      ? menuStore.setMenuGroups.filter((setGroup) => setGroup.ItemCode === props.menu.ItemCode)
      : [];

  const qty = ref(props.qty || 1);
  const selectedTotalOptions = ref<CartMenuOption[]>([]);

  const mainMenuBadgeActive = computed(() => isMainMenuBadgeActive(props.menu));

  // 메뉴 총 금액
  const menuTotalAmount = computed(() => {
    // 옵션 추가금액 계산
    let optionAddPrice = 0;
    optionAddPrice += selectedTotalOptions.value.reduce((acc, option) => {
      if (props.menu.MenuType === 'M') {
        // 토핑 추가금액
        const findTopping = menuStore.getTopping(option.GroupCode.toString(), option.OptionCode);
        return acc + menuStore.getToppingPrice(findTopping, props.menu.IsTax2) * option.Qty;
      } else if (props.menu.MenuType === 'S') {
        // 세트메뉴 추가금액
        const findSetMenu = menuStore.getSetMenu(option.MenuItemCode, Number(option.GroupCode), option.OptionCode);
        return acc + menuStore.getSetMenuPrice(findSetMenu, props.menu.IsTax2) * option.Qty;
      } else {
        return 0;
      }
    }, 0);
    // (메뉴금액 + 추가금액) * 수량 + 세금 (세금별도인 경우)
    return menuStore.getMenuAmount(
      props.menu.FinalPrice,
      optionAddPrice,
      qty.value,
      configStore.config.IsTaxIncluded,
      props.menu.IsTax,
      props.menu.IsTax2,
    );
  });

  // 한정수량
  const limitCount = computed(() => {
    const limit = menuStore.getLimitItem(props.menu.ItemCode)?.OrderLimitCnt;
    if (!limit) return undefined;

    const cartCount = cartStore.cart.CartMenus.find((cartMenu) => cartMenu.ItemCode === props.menu.ItemCode)?.Qty || 0;

    return limit - cartCount;
  });

  const getOptionPrice = (option: CartMenuOption) => {
    if (props.menu.MenuType === 'M') {
      const topping = menuStore.getTopping(option.GroupCode.toString(), option.OptionCode);
      return menuStore.getToppingPrice(topping, props.menu.IsTax2) * option.Qty;
    } else {
      const setMenu = menuStore.getSetMenu(option.MenuItemCode, Number(option.GroupCode), option.OptionCode);
      return menuStore.getSetMenuPrice(setMenu, props.menu.IsTax2) * option.Qty;
    }
  };

  /**
   * 옵션을 변경할 때마다 업데이트
   * 선택한 옵션 리스트에서 변경된 그룹의 옵션을 모두 지운 후 업데이트된 토핑 리스트를 추가
   * @param groupCode 토핑 그룹코드
   * @param selectedOptions 선택된 옵션 리스트
   */
  const handleOptionChange = (groupCode: string | number, selectedOptions: CartMenuOption[]) => {
    const all = [...selectedTotalOptions.value];
    for (let i = 0; i < all.length; i++) {
      if (all[i].GroupCode === groupCode) {
        all.splice(i, 1);
        i--;
      }
    }
    selectedTotalOptions.value = all.concat(selectedOptions);
  };

  /** 닫기 버튼 클릭 */
  const handleCloseClick = () => emits('close');

  /** 장바구니 담기 버튼 클릭 */
  const handleAddCartClick = async () => {
    // 옵션 유효성 검사
    if (props.menu.MenuType === 'M' || props.menu.MenuType === 'S') {
      const invalidGroupNames: string[] = [];
      if (props.menu.MenuType === 'M') {
        // 토핑 메뉴인 경우 최소 수량 체크
        for (const toppingGroup of toppingGroups) {
          const count = selectedTotalOptions.value
            .filter((option) => option.GroupCode === toppingGroup.GroupCode)
            .reduce((accumulator, option) => accumulator + option.Qty, 0);

          if (count < toppingGroup.MinCount) {
            invalidGroupNames.push(getNameByLocale(toppingGroup));
          }
        }
      } else if (props.menu.MenuType === 'S') {
        // 세트 메뉴인 경우 필수 1개 선택 체크
        for (const setMenuGroup of setMenuGroups) {
          const count = selectedTotalOptions.value.filter(
            (option) => option.GroupCode === setMenuGroup.GroupCode,
          ).length;

          if (count < 1) {
            invalidGroupNames.push(getNameByLocale(setMenuGroup));
          }
        }
      }

      // 필수 옵션 검사
      if (invalidGroupNames.length > 0) {
        throw new UserError(`${t('error.required_option')} (${invalidGroupNames.join(', ')})`);
      }
    }

    // 장바구니 데이터 생성
    const menuData: CartMenu = {
      GroupCode: props.menu.GroupCode,
      ItemCode: props.menu.ItemCode,
      Qty: qty.value,
      Toppings: [],
      SetMenus: [],
    };

    // 같은 옵션을 선택했는지 구분하기 위해 옵션값 정렬 후 장바구니 데이터에 추가
    if (props.menu.MenuType === 'M') {
      selectedTotalOptions.value.sort((a, b) => {
        const aGroup = menuStore.getToppingGroup(a.GroupCode.toString());
        const aOption = menuStore.getTopping(a.GroupCode.toString(), a.OptionCode);
        const bGroup = menuStore.getToppingGroup(b.GroupCode.toString());
        const bOption = menuStore.getTopping(b.GroupCode.toString(), b.OptionCode);

        return (
          aGroup?.PositionZ - bGroup?.PositionZ ||
          aGroup?.PositionX - bGroup?.PositionX ||
          aOption.PositionZ - bOption.PositionZ ||
          aOption.PositionY - bOption.PositionY ||
          aOption.PositionX - bOption.PositionX
        );
      });
      menuData.Toppings = selectedTotalOptions.value;
    } else if (props.menu.MenuType === 'S') {
      selectedTotalOptions.value.sort((a, b) => {
        return Number(a.GroupCode) - Number(b.GroupCode) || Number(a.OptionCode) - Number(b.OptionCode);
      });
      menuData.SetMenus = selectedTotalOptions.value;
    }

    // 장바구니에 추가
    const isSuccess = await cartStore.addCartMenu(menuData);
    if (!isSuccess) return;

    // 옵션 선택 페이지 닫기
    emits('close');
  };

  /**
   * 옵션 그룹 이름 조회
   * @param groupCode 옵션 그룹 코드
   */
  const getToppingGroupName = (groupCode: string | number) => {
    if (props.menu.MenuType === 'M') {
      const group = toppingGroups.find((group) => group.GroupCode === groupCode);
      return getNameByLocale(group);
    } else {
      const group = setMenuGroups.find((group) => group.GroupCode === groupCode);
      return getNameByLocale(group);
    }
  };

  /**
   * 옵션 이름 조회
   * @param groupCode 옵션 그룹 코드
   * @param optionCode 옵션 코드
   */
  const getOptionName = (itemCode: string, groupCode: string | number, optionCode: string) => {
    if (props.menu.MenuType === 'M') {
      const topping = menuStore.getTopping(groupCode.toString(), optionCode);
      return getNameByLocale(topping);
    } else {
      const setMenu = menuStore.getSetMenu(itemCode, Number(groupCode), optionCode);
      return getNameByLocale(setMenu);
    }
  };
</script>

<style scoped lang="scss">
  @use '@/assets/scss/variables' as vars;

  .menu-option {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;

    &__left {
      width: 35%;
      padding: 1.0938vw;
    }

    &__center {
      width: 35%;
      border-left: 1px solid #e5e5e5;
      border-right: 1px solid #e5e5e5;
      padding: 1.0938vw;
      overflow-y: auto;
    }

    &__right {
      width: 30%;
      display: flex;
      flex-direction: column;
      gap: 0.7813vw;
      padding-left: 0.9375vw;
      background-color: #f3f4f7;
      padding: 1.0938vw;
    }

    .menu-option__image {
      margin-bottom: 3.125vw;
    }

    .menu-option__name {
      font-size: 1.8657vw;
      font-weight: 500;
      margin-bottom: 0.7813vw;
      word-break: break-all;
      @media (max-width: vars.$sm) {
        font-size: 16px;
      }
    }

    .menu-option__price {
      margin-bottom: 1.9531vw;
      font-size: 2.3881vw;
      font-weight: bold;
      color: var(--point-color-secondary);
      @media (max-width: vars.$sm) {
        font-size: 16px;
      }
    }

    .menu-option__note {
      font-size: 1.194vw;
      color: #000;
      text-align: left;
      word-break: break-all;
      overflow-y: auto;
      @media (max-width: vars.$sm) {
        font-size: 12px;
        line-height: 16px;
        max-height: 80px;
        margin-bottom: 0;
      }
    }

    .menu-option__selected-title {
      font-size: 1.8657vw;
      font-weight: 500;
      @media (max-width: vars.$sm) {
        font-size: 16px;
      }
    }

    .menu-option__selected-list {
      flex: 1;
      overflow-y: auto;
    }

    .menu-option__selected-qty {
      display: flex;
      justify-content: center;
    }

    .menu-option__total-amount {
      display: flex;
      justify-content: space-between;
      padding-top: 0.7813vw;
      border-top: 0.1563vw solid var(--header-vertical-border);
      font-size: 2.3881vw;
      font-weight: bold;

      @media (max-width: vars.$sm) {
        font-size: 16px;
      }

      .menu-option__total-label {
        font-size: 1.8657vw;
      }

      .menu-option__total-value {
        color: var(--point-color-secondary);
      }
    }

    .menu-option__button-group {
      display: grid;
      grid-template-columns: 1fr 2fr;
      gap: 0.7813vw;
    }
  }
</style>
